import { useMemo } from 'react';
import type { DetailPane } from '@/components/DocumentViewer';
import {
  useGetPortalDocumentExtractionQuery,
  useGetPortalDocumentReadUrlQuery,
} from '../apis/portalDocumentsApi';
import ExtractedFieldsTable from '../components/ExtractedFieldsTable';
import { flattenExtractedFields } from '../utils/extracted-fields.util';

/** A portal document's signed read URL plus its extracted fields as the viewer's bottom pane. */
export function usePortalDocumentPreview(documentId: string | null) {
  const readUrl = useGetPortalDocumentReadUrlQuery(documentId ?? '', {
    skip: !documentId,
    refetchOnMountOrArgChange: true,
  });
  const extraction = useGetPortalDocumentExtractionQuery(documentId ?? '', { skip: !documentId });

  const detail: DetailPane | undefined = useMemo(() => {
    if (!documentId) return undefined;
    const rows = flattenExtractedFields(extraction.currentData?.fields ?? null);
    return {
      label: 'Extracted data',
      note: extraction.currentData?.stale ? 'Type corrected, re-extracting' : `${rows.length} fields`,
      content: <ExtractedFieldsTable rows={rows} loading={extraction.isFetching && !extraction.currentData} />,
    };
  }, [documentId, extraction.currentData, extraction.isFetching]);

  return {
    url: readUrl.currentData?.url ?? null,
    contentType: readUrl.currentData?.contentType ?? null,
    fileName: readUrl.currentData?.fileName,
    isLoading: readUrl.isFetching,
    isError: readUrl.isError,
    detail,
  };
}
